import { useCallback } from 'react';

import { ArrowClockwise, ArrowCounterClockwise } from '@phosphor-icons/react/dist/ssr';

import { cn } from '@/utils';

import * as styles from './AudioPlayer.css';
import type { PlayerControlProps } from './AudioPlayer.type';
import PlayButton from './PlayButton';
import { useAudioPlayer } from './useAudioPlayer';
import { formatTime } from './utils';

export function ClassicPlayer({
  audioUrl,
  audioType = 'audio/mpeg',
  thumbnailUrl,
  title,
  duration: knownDuration,
  primaryColor,
  backgroundColor,
  textColor,
  trackColor,
  iconColor: iconColorProp,
  showThumbnail,
  showTitle,
  showRewind,
  showForward,
  rewindSeconds,
  forwardSeconds,
  playButtonSize,
  skipButtonSize,
  progressBarHeight,
  onPlayAttempt,
  isPlaybackLocked,
  audioElementId,
}: PlayerControlProps) {
  const {
    audioRef,
    isPlaying,
    isReady,
    isBuffering,
    currentTime,
    duration,
    progress,
    srcAttached,
    togglePlay,
    handleRewind,
    handleForward,
    seekTo,
  } = useAudioPlayer({ rewindSeconds, forwardSeconds, knownDuration, audioUrl });

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      seekTo(ratio);
    },
    [seekTo],
  );

  const handleProgressKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!duration) return;
      const step = 0.02;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        seekTo(Math.min(1, currentTime / duration + step));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        seekTo(Math.max(0, currentTime / duration - step));
      }
    },
    [duration, currentTime, seekTo],
  );

  const iconColor = iconColorProp || textColor || primaryColor;

  return (
    <div
      className={cn(styles.playerWrapper, isPlaybackLocked && 'pointer-events-none cursor-not-allowed')}
      style={{ backgroundColor }}
    >
      {showThumbnail && thumbnailUrl && <img src={thumbnailUrl} alt="" className={styles.thumbnail} />}

      <div className={styles.playerContent}>
        {showTitle && title && (
          <span className={styles.title} style={{ color: textColor || undefined }}>
            {title}
          </span>
        )}

        <div className={styles.controlsRow}>
          {showRewind && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleRewind}
              disabled={!isReady}
              aria-label={`Rewind ${rewindSeconds} seconds`}
            >
              <ArrowCounterClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {rewindSeconds}
              </span>
            </button>
          )}

          {!isPlaybackLocked && (
            <PlayButton
              isReady={isReady}
              isPlaybackLocked={isPlaybackLocked}
              isPlaying={isPlaying}
              isBuffering={isBuffering}
              playButtonSize={playButtonSize}
              iconColor={iconColor}
              togglePlay={togglePlay}
              onPlayAttempt={onPlayAttempt}
            />
          )}

          {showForward && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleForward}
              disabled={!isReady}
              aria-label={`Forward ${forwardSeconds} seconds`}
            >
              <ArrowClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {forwardSeconds}
              </span>
            </button>
          )}

          <div className={styles.progressRow}>
            <span className={styles.timeLabel} style={{ color: textColor || undefined }}>
              {formatTime(currentTime)}
            </span>

            <div
              className={styles.progressBar}
              style={{ backgroundColor: trackColor, height: progressBarHeight }}
              onClick={handleProgressClick}
              onKeyDown={handleProgressKeyDown}
              role="slider"
              aria-valuenow={currentTime}
              aria-valuemin={0}
              aria-valuemax={duration}
              aria-label="Seek audio"
              tabIndex={0}
            >
              <div
                className={styles.progressFill}
                style={{
                  width: `${progress}%`,
                  backgroundColor: primaryColor,
                }}
              />
            </div>

            <span className={styles.timeLabel} style={{ color: textColor || undefined }}>
              {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>

      {audioUrl && (
        <audio ref={audioRef} id={audioElementId} preload={srcAttached ? 'metadata' : 'none'}>
          <source src={audioUrl} type={audioType} />
          <track kind="captions" src="" label="No captions available" />
        </audio>
      )}
    </div>
  );
}
